"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useDetectClickOutside } from "react-detect-click-outside";
import searchIcon from "@/assets/icons/search-icon.svg";
import crossIcon from "@/assets/icons/crossIcon.svg";
import locationMark from "@/assets/icons/location-mark.svg";
import clock from "@/assets/icons/clock.svg";
import remove from "@/assets/icons/add.svg";
import notFound from "@/assets/icons/location-cross.svg";
import { searchApi } from "@/util/search.util";
import { resultStyle, searchResultStyle } from "@/styles/cva/home.cva";

interface SearchResult {
  address: string;
  state: string;
  placeId: string;
  stateFull: string;
}

interface SearchBarProps {
  className?: string;
  view?: "desktop" | "mobile";
  theme?: "dark" | "light";
  handleClick?: (e: any) => void;
  pageType?: string;
  country?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  className,
  view = "desktop",
  theme = "dark",
  handleClick,
  pageType,
  country,
}) => {
  const router = useRouter();
  const [iconIsClicked, setIconIsClicked] = useState<boolean>(false);
  const [viewResults, setViewResults] = useState<boolean>(false);
  const [placeName, setPlaceName] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<any>([]);

  const clickRef = useDetectClickOutside({
    onTriggered: () => setViewResults(false),
  });

  useEffect(() => {
    const storedSearches = JSON.parse(
      sessionStorage.getItem("recentSearches") || "[]"
    );
    setRecentSearches(storedSearches);
  }, []);

  const debounce = <T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ) => {
    let timer: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearch = debounce(async (text: string) => {
    setPlaceName(text);
    if (text.length > 1) {
      setSearchResults(await searchApi(text, pageType, country, theme));
      setViewResults(true);
    } else if (recentSearches.length) {
      setViewResults(true);
    } else {
      setViewResults(false);
    }
  }, 30);

  const handleResultClick = (result: SearchResult) => {
    try {
      setPlaceName(result.address);
      let country =
        result.address.split(", ")[2] || result.address.split(", ")[1];
      let state = result.stateFull.replaceAll(" ", "-");
      // if (state && state.split(" ").length > 1) {
      //   state = state.split(" ")[0];
      // }
      const path = `/${country == "USA" ? "us/" : "uk/"}${state.toLowerCase().replace(/[^a-z0-9-]/g, "")}${result.address.split(", ").length > 2 &&
          !result.address.includes("Newry,")
          ? "/" +
          result.address.split(", ")[0].replaceAll(" ", "-").toLowerCase()
          : ""
        }`;
      const updatedRecentSearches = [
        result,
        ...recentSearches.filter((r: any) => r.address !== result.address),
      ].slice(0, 5);
      setRecentSearches(updatedRecentSearches);
      sessionStorage.setItem(
        "recentSearches",
        JSON.stringify(updatedRecentSearches)
      );
      router.push(path);
      setViewResults(false);
      setPlaceName("");
    } catch (e) {
      console.error(e);
      setPlaceName("");
    }
  };

  const handleRemoveRecentSearch = (search: string) => {
    const updatedRecentSearches = recentSearches.filter(
      (item: any) => item !== search
    );
    setRecentSearches(updatedRecentSearches);
    sessionStorage.setItem(
      "recentSearches",
      JSON.stringify(updatedRecentSearches)
    );
  };

  const renderSearchInput = (
    iconSize: number,
    extraClasses: string = "",
    isMobile: boolean = false
  ) =>
    !isMobile ? (
      <div className={`flex w-full ${extraClasses}`}>
        <span className="pt-2.5">
          <Image
            src={searchIcon}
            alt="search icon"
            width={iconSize}
            loading="eager"
            priority
          />
        </span>
        <input
          type="search"
          placeholder="Search state, county, city or zip code"
          className="p-2 w-full appearance-none outline-none text-white bg-transparent font-inter"
          value={placeName}
          onChange={(e) => handleSearch(e.target.value)}
          onClick={() => setViewResults(true)}
        />
      </div>
    ) : (
      <div className={`flex w-full ${extraClasses}`}>
        <span className="pt-2.5">
          <Image
            src={searchIcon}
            alt="search icon"
            width={iconSize}
            loading="eager"
            priority
          />
        </span>
        <input
          type="text"
          placeholder="Search state, county, city or zip code"
          className="p-2 w-full appearance-none outline-none text-white bg-transparent font-inter"
          value={placeName}
          onChange={(e) => handleSearch(e.target.value)}
          onClick={() => setViewResults(true)}
        />
      </div>
    );

  const handleGraphSearch = (city: any) => {
    handleClick && handleClick(city);
    setViewResults(false);
    setPlaceName("");
  };
  const inputRef: any = useRef(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current?.focus();
    }
  }, [inputRef]);

  return theme == "light" ? (
    <section>
      <div className="min-w-[10rem] border rounded-lg">
        <input
          type="search"
          ref={inputRef}
          placeholder="Search"
          className="p-2 w-full appearance-none outline-none text-neutral-dark-100 bg-transparent font-inter caret-black focused"
          value={placeName}
          onChange={(e) => handleSearch(e.target.value)}
          onClick={() => setViewResults(true)}
        />
      </div>
      {viewResults &&
        (searchResults && searchResults.length > 0 ? (
          <div ref={clickRef} className={searchResultStyle()}>
            {searchResults.map((city, index) => (
              <div
                key={index}
                className="flex cursor-pointer px-4 py-2 space-x-2 hover:bg-neutral-light-100"
                onClick={() => handleGraphSearch(city)}
              >
                <Image src={locationMark} alt="results" />
                <p>{city.address}</p>
              </div>
            ))}
          </div>
        ) : (
          placeName.length > 1 && (
            <div
              ref={clickRef}
              className={searchResultStyle({ type: "notFound" })}
            >
              <div className="flex px-4 py-2 space-x-2">
                <Image src={notFound} alt="no-data" />
                <p>No results found</p>
              </div>
            </div>
          )
        ))}
    </section>
  ) : (
    <div className={`${view === "mobile" ? "" : "relative"}`}>
      {view === "mobile" ? (
        <Image
          src={searchIcon}
          alt="search icon"
          width={30}
          className="mt-1.5"
          onClick={() => setIconIsClicked(!iconIsClicked)}
        />
      ) : (
        <div
          className={`bg-dark-neutral-200 flex py-0.5 px-3 rounded self-stretch gap-x-3 ${className}`}
        >
          {renderSearchInput(20)}
        </div>
      )}
      {view === "mobile" && iconIsClicked && (
        <section className="flex justify-between absolute top-4 inset-x-0 mx-4 p-2 rounded bg-dark-neutral-200">
          {renderSearchInput(20, "space-x-4 pl-2", view === "mobile")}
          <Image
            src={crossIcon}
            alt="cross"
            onClick={() => {
              setPlaceName("");
              setViewResults(false);
              setIconIsClicked(!iconIsClicked);
            }}
          />
        </section>
      )}
      {viewResults &&
        placeName.length < 2 &&
        recentSearches.length > 0 &&
        searchResults &&
        searchResults.length === 0 && (
          <div ref={clickRef} className={resultStyle({ type: view })}>
            {recentSearches.map((search: any, index: any) => (
              <div
                key={index}
                className="flex justify-between cursor-pointer px-4 py-2 hover:bg-dark-neutral-200"
                onClick={() => handleResultClick(search)}
              >
                <div className="flex space-x-2">
                  <Image src={clock} alt="recent-search" />
                  <p>{search.address}</p>
                </div>
                <Image
                  src={remove}
                  alt="remove"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveRecentSearch(search);
                  }}
                />
              </div>
            ))}
          </div>
        )}
      {viewResults &&
        (searchResults && searchResults.length > 0 ? (
          <div ref={clickRef} className={resultStyle({ type: view })}>
            {searchResults.map((city, index) => (
              <div
                key={index}
                className="flex cursor-pointer px-4 py-2 space-x-2 hover:bg-dark-neutral-200"
                onClick={() => handleResultClick(city)}
              >
                <Image src={locationMark} alt="results" />
                <p>{city.address}</p>
              </div>
            ))}
          </div>
        ) : (
          placeName.length > 2 && (
            <div ref={clickRef} className={resultStyle({ type: view })}>
              <div className="flex px-4 py-2 space-x-2">
                <Image src={notFound} alt="no-data" />
                <p>No results found</p>
              </div>
            </div>
          )
        ))}
    </div>
  );
};

export default SearchBar;
